import { AsyncPipe } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component, computed,
  inject,
  input,
  InputSignal,
  output,
  OutputEmitterRef, Signal,
  signal,
  WritableSignal,
} from '@angular/core';
import { toObservable, toSignal } from '@angular/core/rxjs-interop';
import { PageEvent } from '@angular/material/paginator';
import { PaginatorComponent } from '@libs/common/components';
import { IconComponent } from '@libs/components/icons';
import { CategorySideMenuComponent } from '@libs/components/navigation';
import { FlashMessageModule, FlashMessageService, SpinnerOverlayService } from '@libs/shared/common';
import { CatalogSearchFilterSortValueModel, CatalogSearchResultModel, SearchInputModel } from '@libs/shop/models';
import { NavItemModel} from '@libs/shared/models';
import {
  CatalogSearchRepositoryService,
  PageViewService,
} from '@libs/shared/services';
import { DeferredPageInfoTracking } from '@libs/shared/tracking';
import { catchError, filter, Observable, of, switchMap, tap } from 'rxjs';
import { ProductListSkeletonComponent } from '../catalog/product-list/product-list-skeleton/product-list-skeleton.component';
import { ProductListComponent } from '../catalog/product-list/product-list.component';
import { AddToShoppingListDialogComponent } from '../shopping-list/add-to-shopping-list-dialog/add-to-shopping-list-dialog.component';
import { CatalogSearchFilterSortComponent } from './catalog-search-filter-sort/catalog-search-filter-sort.component';
import { ProductCategoryBannerComponent } from './components/product-category-banner';

@Component({
  selector: 'ui-catalog-search',
  templateUrl: './catalog-search.component.html',
  styleUrls: ['catalog-search.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    AsyncPipe,
    FlashMessageModule,
    PaginatorComponent,
    ProductListComponent,
    ProductListSkeletonComponent,
    AddToShoppingListDialogComponent,
    IconComponent,
    CatalogSearchFilterSortComponent,
    CategorySideMenuComponent,
    ProductCategoryBannerComponent,
  ],
})
export class CatalogSearchComponent {
  public searchParams: InputSignal<SearchInputModel | undefined> = input();
  public searchParamsUpdate: OutputEmitterRef<SearchInputModel> = output();
  public initialSearch: WritableSignal<boolean> = signal(true);
  public searchParamsHasQuery: Signal<boolean> = computed(() => {
    const searchParams: SearchInputModel | undefined = this.searchParams();

    return !!searchParams?.q?.length;
  });

  public searchParams$: Observable<SearchInputModel | undefined> = toObservable(this.searchParams);
  public currentNavigationNode: Signal<NavItemModel | undefined> = toSignal(this.pageViewService.getCurrentNode());
  public searchResults$: Observable<CatalogSearchResultModel | undefined> = this.searchParams$.pipe(
    filter(Boolean),
    tap(() => {
      if (!this.initialSearch()) {
        this.spinnerOverlay.showLoader();
      }
    }),
    switchMap(searchParams => this.catalogSearchRepositoryService.search(searchParams).pipe(
      catchError(() => {
        this.messageService.showMessage('errorSearchResult');

        return of(undefined);
      }),
    )),
    tap((result) => {
      this.initialSearch.set(false);
      this.spinnerOverlay.hideLoader();
      this.deferredPageInfoTracking.complete({
        searchTerm: this.searchParams()?.q ?? null,
        numSearchResults: result?.pagination?.found ?? null,
      });
    }),
  );

  private readonly deferredPageInfoTracking: DeferredPageInfoTracking = inject(DeferredPageInfoTracking);

  public constructor(
    private readonly catalogSearchRepositoryService: CatalogSearchRepositoryService,
    private readonly pageViewService: PageViewService,
    private readonly messageService: FlashMessageService,
    private readonly spinnerOverlay: SpinnerOverlayService,
  ) {
  }

  public onPageChange($event: PageEvent): void {
    this.searchParamsUpdate.emit({
      ...this.searchParams(),
      page: $event.pageIndex,
      ipp: $event.pageSize,
    });
  }

  public onFilterSortChange($event: CatalogSearchFilterSortValueModel): void {
    this.searchParamsUpdate.emit({
      ...this.searchParams(),
      sort: $event.sort,
      label: $event.labelFilterValue,
      manufacturer: $event.manufacturerFilterValue,
      own_brand: $event.ownBrandFilterValue,
      page: 0,
    });
  }

  public onSideMenuItemSelect($event: NavItemModel): void {
    const newSearchParams: SearchInputModel =
      {
        ...this.searchParams(),
        category: $event.id?.toString(),
        page: 0,
      };

    this.searchParamsUpdate.emit(newSearchParams);
  }
}
